import {
  type MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { UseFormGetValues, UseFormSetError } from 'react-hook-form';

import { HttpStatusCode } from 'axios';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore lodash is in the parent dir
import _ from 'lodash';
import { Result, err } from 'neverthrow';

import { QuickQuoteConfig } from 'contexts/serviceContext';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { useToast } from 'hooks/useToaster';
import {
  SUBMIT_PORTAL_BID_RESULT_POLL_CANCELLED_MESSAGE,
  type SubmitPortalBidRequestBody,
  type SubmitPortalBidResponseBody,
  getSubmitPortalBidResult,
  submitPortalBid,
} from 'lib/api/submitPortalBid';
import {
  QuoteTerminatingAction,
  buildUserConfirmedQuoteBody,
  quoteValueTypeToApi,
  updateQuoteRequestSuggestion,
} from 'lib/api/updateQuoteRequestSuggestion';
import { getCurrentTab, getTabHTML } from 'lib/chromeScripting/util';
import { QuotingPortal } from 'lib/hosts/quoting/interface';
import { DraftReplyMode } from 'lib/mailclient/interface';
import {
  getServerSubmitPortalNamesForQuoteMode,
  isServerSubmitPortalEnabledForQuoteMode,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/common/submitBidPortalPolicy';
import {
  QuoteValueType,
  SUBMIT_PORTAL_BID_RESULT_POLL_TIMEOUT_MESSAGE,
  SUBMIT_PORTAL_BID_USER_FACING_FAILURE_MESSAGE,
  SubmitBidStatusVariant,
  getSidebarServerSubmitBidResultPollIntervalMs,
  getSidebarServerSubmitBidResultPollMaxAttempts,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/constants';
import { Email } from 'types/Email';
import { Maybe, Undef } from 'types/UtilityTypes';
import { ApiError } from 'types/api/ApiError';
import {
  PortalActionResult,
  SubmitQuoteToPortalMessage,
} from 'types/chromescript/QuotingPortal';
import { integrationNameMap } from 'types/enums/Integrations';
import { SuggestionPipelines } from 'types/suggestions/CoreSuggestions';
import { SuggestionStatus } from 'types/suggestions/LoadSuggestions';
import type { SuggestedQuoteChange } from 'types/suggestions/QuoteSuggestions';
import captureException from 'utils/captureException';
import { applyCompleteKitSuggestions } from 'utils/completeKitSuggestion';
import { isHTMLContent } from 'utils/copyToClipboard';
import { calculatePricing } from 'utils/priceCalculations';

import { useQuickQuoteContext } from '../context/QuickQuoteContext';
import { CarrierCostType, QuickQuoteInputs, QuoteMode } from '../types';
import { buildPortalSubmitBidAmountStrings } from '../util';
import { processQuoteTMSSubmission, sendUserQuote } from '../utils';
import { getSubmitBidLoadIdNormalizer } from '../utils/getSubmitBidLoadIdNormalizer';
import { resolveNormalizedSubmitBidLoadId } from '../utils/resolveNormalizedSubmitBidLoadId';
import {
  QuoteReplyAction,
  useQuoteReplyMailClient,
} from './useQuoteReplyMailClient';

const sleep = (ms: number, signal?: AbortSignal): Promise<void> => {
  if (!signal) {
    return new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  }

  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);

    const onAbort = () => {
      clearTimeout(timer);
      signal.removeEventListener('abort', onAbort);
      reject(new DOMException('Aborted', 'AbortError'));
    };

    signal.addEventListener('abort', onAbort);
  });
};

const formatDraftBodyForMail = (latestDraftBody: string): string =>
  isHTMLContent(latestDraftBody)
    ? latestDraftBody.trim()
    : latestDraftBody.trim().replace(/\n/g, '<br>');

const hasToRecipients = (to: Undef<string[]>): boolean => {
  if (to === undefined) {
    return true;
  }

  return to.length > 0;
};

/**
 * Sidebar submit-bid overrides for `handleSubmitToPortal` (Drumkit portal submit-bid).
 * Field nullability matches `SubmitBidPayload` from `SubmitBidSection` / `QuickQuoteForm`
 * (`Maybe` = `null` when unset from the submit-bid UI).
 */
export type SubmitPortalOverrides = {
  selectedIntegrationName?: Maybe<string>;
  credentialId?: Maybe<number>;
  integrationId?: Maybe<number>;
  // FTL only: passed from quick-quote fuel estimate for line-haul split in `buildPortalSubmitBidAmountStrings`.
  fuelEstimate?: Undef<number>;
};

// When set, portal submit bid surfaces messages here instead of only toasts.
export type PortalSubmitInlineFeedback = {
  // User-visible error/warning/info text. Pass `null` to clear.
  // When `message` is set, `variant` controls styling in `SubmitBidSection` (defaults to `destructive` in callers that omit it).
  setUserMessage: (
    message: Maybe<string>,
    // Prefer `Warning` / `Info` / `Destructive`; avoid `Success` here (use `setSuccessMessage`).
    variant?: SubmitBidStatusVariant
  ) => void;
  setSuccessMessage?: (message: Maybe<string>) => void;
  // True while submit-bid result polling is returning HTTP 202 (job still running).
  setIsSubmitBidResultPending?: (pending: boolean) => void;
};

/**
 * Hook that provides all handler functions for quote actions
 */
export const useQuoteHandlers = (
  email: Maybe<Email>,
  getValues: UseFormGetValues<QuickQuoteInputs>,
  setError: UseFormSetError<QuickQuoteInputs>,
  getSelectedQuickQuoteId: () => Undef<number>,
  portal: Undef<QuotingPortal>,
  setIsSubmittingToPortal: (val: boolean) => void,
  quickQuoteConfig: Maybe<QuickQuoteConfig>,
  /** When current, blocks email load-id fallback after the user dirties `loadId`. */
  submitBidLoadIdDirtyRef?: MutableRefObject<boolean>
) => {
  const { toast } = useToast();

  const submitPortalBidResultPollAbortRef = useRef<AbortController | null>(
    null
  );

  useEffect(() => {
    return () => {
      submitPortalBidResultPollAbortRef.current?.abort();
    };
  }, []);

  const {
    serviceFeaturesEnabled: {
      isQuoteSubmissionToServiceEnabled,
      isTMSQuoteSubmissionEnabled,
      isBiddingPortalSubmitBidEnabled,
    },
  } = useServiceFeatures();

  const {
    currentState: {
      clickedSuggestion,
      threadReferences,
      inReplyTo,
      threadItemId,
      tabId,
    },
    setCurrentState,
  } = useContext(SidebarStateContext);

  const { canDraftReply, canSendReply, replyWithMailClient } =
    useQuoteReplyMailClient();

  const context = useQuickQuoteContext();
  const {
    quoteResponse,
    parentQuoteRequestId,
    greenscreensQuoteID,
    selectedLTLQuotes,
    carrierCost,
    carrierCostType,
    profit,
    profitType,
    finalFlatPrice,
    maxDistance,
    draftResponse,
    isSubmitToTMS,
    quoteMode,
    finalPriceFormat,
    setCreatedQuoteId,
    setCreatedQuoteInternalId,
    setSubmitQuoteLoading,
    setLoadingDraftReply,
  } = context;

  /**
   * Handle terminating action - updates quote request with final values
   * Called when user submits to portal, TMS, creates draft, or copies
   * @param terminatingAction - reply, copy, input, or submit
   * @param quotedValueType - linehaul or total_sell (from format button / copy button / portal)
   */
  const handleTerminatingAction = useCallback(
    async (
      terminatingAction: Undef<QuoteTerminatingAction> = undefined,
      quotedValueType: Undef<QuoteValueType> = undefined
    ) => {
      const { flatCarrierCost: finalFlatCarrierCost, finalProfit } =
        calculatePricing(
          carrierCost,
          carrierCostType,
          profit,
          profitType,
          maxDistance
        );

      const resolvedQuoteRequestId =
        parentQuoteRequestId || clickedSuggestion?.id || 0;

      if (isQuoteSubmissionToServiceEnabled) {
        sendUserQuote({
          email,
          quoteResponse,
          parentQuoteRequestId: resolvedQuoteRequestId,
          greenscreensQuoteID,
          carrierCost: _.round(finalFlatCarrierCost),
          profit: _.round(finalProfit),
          finalPrice: finalFlatPrice ?? 0,
          draftResponse,
          profitType,
        });
      }

      const currentSelectedQuickQuoteId = getSelectedQuickQuoteId();
      const selectedLtlQuickQuoteIds =
        quoteMode === QuoteMode.LTL
          ? selectedLTLQuotes
              .map((quote) => {
                const rawId =
                  (quote as unknown as { id?: number | string }).id ??
                  (quote as unknown as { ID?: number | string }).ID;
                return Number(rawId);
              })
              .filter((id) => Number.isFinite(id) && id > 0)
          : undefined;

      const result = await updateQuoteRequestSuggestion(
        resolvedQuoteRequestId,
        SuggestionStatus.Accepted,
        buildUserConfirmedQuoteBody({
          finalQuotePrice: _.round(finalFlatPrice ?? 0),
          finalMargin: _.round(finalProfit),
          marginType: profitType,
          finalCarrierCost: _.round(finalFlatCarrierCost),
          carrierCostType: carrierCostType,
          customerExternalTMSId: getValues('customerName'),
          ...(terminatingAction && {
            quoteTerminatingAction: terminatingAction,
          }),
          ...(quotedValueType && {
            quotedValueType: quoteValueTypeToApi(quotedValueType),
          }),
        }),
        currentSelectedQuickQuoteId,
        selectedLtlQuickQuoteIds
      );

      if (result.isErr()) {
        captureException(result.error, {
          functionName: 'handleTerminatingAction',
        });
        return;
      }

      // Kit Done keys off suggestion id; PATCH above uses quoteRequestId.
      const completedId =
        clickedSuggestion?.id ??
        (resolvedQuoteRequestId || parentQuoteRequestId);
      if (completedId) {
        setCurrentState((prevState) => ({
          ...prevState,
          ...applyCompleteKitSuggestions(
            prevState,
            completedId,
            SuggestionPipelines.QuickQuote
          ),
        }));
      }
    },
    [
      carrierCost,
      carrierCostType,
      profit,
      profitType,
      maxDistance,
      isQuoteSubmissionToServiceEnabled,
      email,
      quoteResponse,
      parentQuoteRequestId,
      clickedSuggestion,
      greenscreensQuoteID,
      selectedLTLQuotes,
      finalFlatPrice,
      draftResponse,
      getSelectedQuickQuoteId,
      getValues,
      quoteMode,
      setCurrentState,
    ]
  );

  /**
   * Polls backend `POST /integrations/portal/submit-bid/result` after an async
   * sidebar server submit-bid.
   */
  const waitForSubmitPortalBidResult = useCallback(
    async (
      jobId: string,
      setIsSubmitBidResultPending?: (pending: boolean) => void
    ): Promise<Result<SubmitPortalBidResponseBody, ApiError>> => {
      submitPortalBidResultPollAbortRef.current?.abort();
      const ac = new AbortController();
      submitPortalBidResultPollAbortRef.current = ac;
      const { signal } = ac;

      let notified202 = false;
      const maxAttempts = getSidebarServerSubmitBidResultPollMaxAttempts();
      try {
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          if (signal.aborted) {
            return err({
              message: SUBMIT_PORTAL_BID_RESULT_POLL_CANCELLED_MESSAGE,
            });
          }

          const result = await getSubmitPortalBidResult({ jobId, signal });

          if (result.isOk()) {
            return result;
          }

          if (
            result.error.message ===
            SUBMIT_PORTAL_BID_RESULT_POLL_CANCELLED_MESSAGE
          ) {
            return result;
          }

          if (result.error.status !== 202) {
            return result;
          }

          if (!notified202) {
            notified202 = true;
            setIsSubmitBidResultPending?.(true);
          }

          if (signal.aborted) {
            return err({
              message: SUBMIT_PORTAL_BID_RESULT_POLL_CANCELLED_MESSAGE,
            });
          }

          try {
            await sleep(
              getSidebarServerSubmitBidResultPollIntervalMs(attempt),
              signal
            );
          } catch (e: unknown) {
            if (e instanceof DOMException && e.name === 'AbortError') {
              return err({
                message: SUBMIT_PORTAL_BID_RESULT_POLL_CANCELLED_MESSAGE,
              });
            }
            throw e;
          }
        }

        return err({
          message: SUBMIT_PORTAL_BID_RESULT_POLL_TIMEOUT_MESSAGE,
          status: HttpStatusCode.GatewayTimeout,
        });
      } finally {
        if (!signal.aborted) {
          setIsSubmitBidResultPending?.(false);
        }
        if (submitPortalBidResultPollAbortRef.current === ac) {
          submitPortalBidResultPollAbortRef.current = null;
        }
      }
    },
    []
  );

  /**
   * Submit quote to TMS
   */
  const handleSubmitQuoteToTMS = useCallback(async () => {
    if (isTMSQuoteSubmissionEnabled && isSubmitToTMS) {
      const customerId = getValues('customerName');

      if (!customerId) {
        toast({
          description: 'Please enter a customer name.',
          variant: 'destructive',
        });
        return;
      }

      setSubmitQuoteLoading(true);

      const createdQuote = await processQuoteTMSSubmission({
        customerId: customerId,
        finalPrice: finalFlatPrice,
        getValues,
        setError,
      });

      const createdQuoteId = createdQuote?.quoteExternalId ?? '';
      const createdQuoteInternalId =
        createdQuote?.quoteId != null && createdQuote.quoteId > 0
          ? createdQuote.quoteId
          : null;

      setCreatedQuoteId(createdQuoteId);
      setCreatedQuoteInternalId(createdQuoteInternalId);
      setSubmitQuoteLoading(false);
    }
  }, [
    isTMSQuoteSubmissionEnabled,
    isSubmitToTMS,
    getValues,
    toast,
    setSubmitQuoteLoading,
    finalFlatPrice,
    setError,
    setCreatedQuoteId,
    setCreatedQuoteInternalId,
  ]);

  const quotedValueTypeForReply =
    finalPriceFormat === CarrierCostType.Linehaul
      ? QuoteValueType.Linehaul
      : QuoteValueType.TotalSell;

  // Create draft response in the host mail client (Outlook or Front)
  const handleDraftResponse = useCallback(
    async (
      latestDraftBody: string,
      replyMode: DraftReplyMode,
      to?: string[],
      cc?: string[],
      bcc?: string[]
    ) => {
      if (!canDraftReply) {
        return;
      }

      if (!hasToRecipients(to)) {
        toast({
          description:
            'Add at least one To recipient before creating a draft reply.',
          variant: 'warning',
        });
        return;
      }

      if (
        quoteMode !== QuoteMode.LTL &&
        (!finalFlatPrice || finalFlatPrice <= 0)
      ) {
        toast({
          description: 'Set a valid quote price before creating a draft reply.',
          variant: 'warning',
        });
        return;
      }

      // TODO: Warn user to configure signature in Portal if none found
      await replyWithMailClient({
        action: QuoteReplyAction.Draft,
        reply: {
          threadItemId,
          draftBody: formatDraftBodyForMail(latestDraftBody),
          replyMode,
          to,
          cc,
          bcc,
        },
        setLoading: setLoadingDraftReply,
        onReplied: () =>
          handleTerminatingAction(
            QuoteTerminatingAction.Reply,
            quotedValueTypeForReply
          ),
        functionName: 'handleDraftResponse',
      });
    },
    [
      canDraftReply,
      quoteMode,
      finalFlatPrice,
      quotedValueTypeForReply,
      setLoadingDraftReply,
      threadItemId,
      handleTerminatingAction,
      replyWithMailClient,
      toast,
    ]
  );

  const handleSendResponse = useCallback(
    async (
      latestDraftBody: string,
      replyMode: DraftReplyMode,
      to?: string[],
      cc?: string[],
      bcc?: string[]
    ) => {
      if (!canSendReply) {
        return;
      }

      if (!hasToRecipients(to)) {
        toast({
          description:
            'Add at least one To recipient before sending the quote reply.',
          variant: 'warning',
        });
        return;
      }

      if (
        quoteMode !== QuoteMode.LTL &&
        (!finalFlatPrice || finalFlatPrice <= 0)
      ) {
        toast({
          description:
            'Set a valid quote price before sending the quote reply.',
          variant: 'warning',
        });
        return;
      }

      await replyWithMailClient({
        action: QuoteReplyAction.Send,
        reply: {
          threadItemId,
          draftBody: formatDraftBodyForMail(latestDraftBody),
          replyMode,
          to,
          cc,
          bcc,
        },
        setLoading: setLoadingDraftReply,
        onReplied: () =>
          handleTerminatingAction(
            QuoteTerminatingAction.Reply,
            quotedValueTypeForReply
          ),
        functionName: 'handleSendResponse',
      });
    },
    [
      canSendReply,
      quoteMode,
      finalFlatPrice,
      quotedValueTypeForReply,
      setLoadingDraftReply,
      threadItemId,
      handleTerminatingAction,
      replyWithMailClient,
      toast,
    ]
  );

  /**
   * Submit quote to portal (e.g., E2Open, FreightView, UberFreight, Shipwell, etc)
   * Sidebar server submit-bid (e.g. quote-request email + detected bidding portal):
   * optional `overrides` + `inlineFeedback` -> `POST /integrations/portal/submit-bid`
   * and poll `/submit-bid/result` while HTTP 202.
   */
  const handleSubmitToPortal = useCallback(
    async (
      quote: Maybe<number>,
      quoteValueType: QuoteValueType,
      overrides?: SubmitPortalOverrides,
      inlineFeedback?: PortalSubmitInlineFeedback
    ) => {
      const setInline = inlineFeedback?.setUserMessage;
      const setSuccessInline = inlineFeedback?.setSuccessMessage;

      const failInline = (
        message: string,
        variant: SubmitBidStatusVariant = SubmitBidStatusVariant.Warning
      ) => {
        setSuccessInline?.(null);
        setInline?.(message, variant);
        if (!setInline) {
          toast({ description: message, variant });
        }
      };

      const isServerSubmitAttempt =
        isBiddingPortalSubmitBidEnabled && overrides != null;

      if (isServerSubmitAttempt) {
        const supportedPortalsForMode =
          getServerSubmitPortalNamesForQuoteMode(quoteMode);
        const supportedPortalLabels = supportedPortalsForMode.map(
          (portalName) => integrationNameMap[portalName] ?? portalName
        );
        const selectedName = overrides.selectedIntegrationName?.trim();

        if (!selectedName) {
          failInline(
            'Select a bidding portal and a saved login, then try again.',
            SubmitBidStatusVariant.Info
          );
          captureException(
            'handleSubmitToPortal: server submit enabled but selectedIntegrationName missing',
            { functionName: 'handleSubmitToPortal' }
          );
          return;
        }

        const selectedPortalLabel =
          integrationNameMap[selectedName as keyof typeof integrationNameMap] ??
          selectedName;

        if (
          supportedPortalsForMode.length === 0 ||
          !isServerSubmitPortalEnabledForQuoteMode(selectedName, quoteMode)
        ) {
          const quoteModeLabel = quoteMode === QuoteMode.LTL ? 'LTL' : 'TL';
          const supportedPortalLabelText =
            supportedPortalLabels.length > 0
              ? supportedPortalLabels.join(', ')
              : 'none';
          failInline(
            `Submitting from the sidebar is not enabled for ${selectedPortalLabel} on ${quoteModeLabel} quotes. Supported portal(s): ${supportedPortalLabelText}.`,
            SubmitBidStatusVariant.Info
          );
          captureException(
            `handleSubmitToPortal: unsupported server submit portal (${selectedName}) for quoteMode (${quoteMode})`,
            { functionName: 'handleSubmitToPortal' }
          );
          return;
        }

        if (!quote) {
          failInline(
            'No quote amount to submit. Check your pricing, then try again.',
            SubmitBidStatusVariant.Info
          );
          return;
        }

        const credId = overrides.credentialId;
        const integId = overrides.integrationId;
        if (credId == null || credId <= 0 || integId == null || integId <= 0) {
          failInline(
            `Select a saved ${selectedPortalLabel} login for submit bid, then try again.`,
            SubmitBidStatusVariant.Info
          );
          return;
        }

        const quoteSuggestion =
          clickedSuggestion as Undef<SuggestedQuoteChange>;
        const inferEmailWhenFormLoadIdEmpty =
          submitBidLoadIdDirtyRef == null || !submitBidLoadIdDirtyRef.current;
        const loadId = resolveNormalizedSubmitBidLoadId(
          String(getValues('loadId') ?? ''),
          email,
          {
            inferEmailWhenFormLoadIdEmpty,
            normalizeLoadId: getSubmitBidLoadIdNormalizer(selectedName),
          }
        );

        if (!loadId) {
          failInline(
            `Enter the ${selectedPortalLabel} load number you would like to bid on, then try again.`,
            SubmitBidStatusVariant.Info
          );
          return;
        }

        // Submit-bid from QuickQuoteForm passes inlineFeedback and
        // owns isSubmittingToPortal for the full async lifecycle
        const portalSpinnerOwnedByCaller = inlineFeedback != null;
        if (!portalSpinnerOwnedByCaller) {
          setIsSubmittingToPortal(true);
        }

        const portalAction: QuoteTerminatingAction =
          quickQuoteConfig?.isSubmitOnPortalEnabled
            ? QuoteTerminatingAction.Submit
            : QuoteTerminatingAction.Input;

        handleTerminatingAction(portalAction, quoteValueType);

        try {
          const tmsRaw =
            quoteSuggestion?.suggested?.customerExternalTMSID?.trim();
          let tmsCustomerId: Undef<number>;
          if (tmsRaw) {
            const parsed = Number.parseInt(tmsRaw, 10);
            if (Number.isFinite(parsed) && parsed > 0) {
              tmsCustomerId = parsed;
            }
          }

          const ltlQuote =
            quoteMode === QuoteMode.LTL && selectedLTLQuotes.length > 0
              ? selectedLTLQuotes[0]
              : undefined;

          const portalBidAmounts = buildPortalSubmitBidAmountStrings(quote, {
            quoteMode,
            fuelEstimate: overrides.fuelEstimate,
            ltlQuote,
          });

          const resolvedQuoteRequestId =
            parentQuoteRequestId ||
            clickedSuggestion?.id ||
            quoteResponse?.quoteRequestId ||
            0;

          const submitPortalBidBody: SubmitPortalBidRequestBody = {
            credentialId: credId,
            integrationId: integId,
            loadId,
            ...portalBidAmounts,
            startUrl: quoteSuggestion?.sourceURL?.trim() || undefined,
            tmsCustomerId,
            customerName: getValues('customerName')?.trim() || undefined,
            threadReferences: threadReferences?.trim() || undefined,
            inReplyTo: inReplyTo?.length ? inReplyTo : undefined,
            emailId: email?.id,
            quoteRequestId: resolvedQuoteRequestId || undefined,
          };
          const result = await submitPortalBid(submitPortalBidBody);

          if (result.isErr()) {
            failInline(
              SUBMIT_PORTAL_BID_USER_FACING_FAILURE_MESSAGE,
              SubmitBidStatusVariant.Destructive
            );
            return;
          }

          const completionResult = await waitForSubmitPortalBidResult(
            result.value.jobId,
            inlineFeedback?.setIsSubmitBidResultPending
          );
          if (
            completionResult.isErr() &&
            completionResult.error.message ===
              SUBMIT_PORTAL_BID_RESULT_POLL_CANCELLED_MESSAGE
          ) {
            inlineFeedback?.setIsSubmitBidResultPending?.(false);
            return;
          }
          if (completionResult.isErr()) {
            const apiMessage =
              typeof completionResult.error.message === 'string'
                ? completionResult.error.message
                : '';
            const userMessage =
              apiMessage === SUBMIT_PORTAL_BID_RESULT_POLL_TIMEOUT_MESSAGE
                ? SUBMIT_PORTAL_BID_RESULT_POLL_TIMEOUT_MESSAGE
                : SUBMIT_PORTAL_BID_USER_FACING_FAILURE_MESSAGE;

            // `waitForSubmitPortalBidResult` keeps polling on 202, only non-202 errors
            // (or client timeout as 504) surface here.
            failInline(userMessage, SubmitBidStatusVariant.Destructive);
            return;
          }

          setInline?.(null);
          setSuccessInline?.('Bid submitted successfully');
          if (!setSuccessInline) {
            toast({
              description: 'Bid submitted successfully',
              variant: 'success',
            });
          }
        } catch (error: unknown) {
          captureException(error, { functionName: 'handleSubmitToPortal' });
          failInline(
            SUBMIT_PORTAL_BID_USER_FACING_FAILURE_MESSAGE,
            SubmitBidStatusVariant.Destructive
          );
        } finally {
          if (!portalSpinnerOwnedByCaller) {
            setIsSubmittingToPortal(false);
          }
        }
        return;
      }

      if (!portal) {
        captureException(
          'Submit to Portal button clicked but not on a supported portal, this should not happen',
          {
            functionName: 'handleSubmitToPortal',
          }
        );

        toast({
          description: 'Not on a supported portal.',
          variant: 'warning',
        });
        return;
      }

      if (
        // NOTE: Button disabled attribute not used because webpage may change without sidepanel change
        !portal.canSubmit(
          await getCurrentTab(),
          (await getTabHTML(tabId, portal.allFrames)) ?? ''
        )
      ) {
        toast({
          description:
            'Submission is not supported for this page. Please navigate to submission page.',
          variant: 'warning',
        });
        return;
      }

      // `!quote` misses negatives (e.g. line haul = total - fuel when fuel > total).
      if (quote == null || !Number.isFinite(quote) || quote <= 0) {
        toast({
          description:
            quote != null && Number.isFinite(quote) && quote <= 0
              ? 'Quote must be greater than $0. Try Input total sell, or raise the rate above fuel.'
              : 'No quote to submit.',
          variant: 'info',
        });
        return;
      }

      setIsSubmittingToPortal(true);

      const portalAction: QuoteTerminatingAction =
        quickQuoteConfig?.isSubmitOnPortalEnabled
          ? QuoteTerminatingAction.Submit
          : QuoteTerminatingAction.Input;

      // Non-blocking call to handleTerminatingAction
      handleTerminatingAction(portalAction, quoteValueType);

      try {
        const message: SubmitQuoteToPortalMessage = {
          targetTabId: tabId,
          action: portal.submitAction,
          data: {
            distance: maxDistance,
            flatRate: _.round(quote),
            isSubmitOnPortalEnabled:
              quickQuoteConfig?.isSubmitOnPortalEnabled || false,
            ...(quoteValueType ? { quoteType: quoteValueType } : {}),
          },
        };

        const sendMessage = () =>
          new Promise((resolve, reject) => {
            try {
              chrome.runtime.sendMessage(message, (response) => {
                if (chrome.runtime.lastError) {
                  reject(chrome.runtime.lastError);
                } else if (response && response.success) {
                  resolve(response);
                } else {
                  reject(response);
                }
              });
            } catch (err) {
              reject(err);
            }
          }) as Promise<PortalActionResult>;

        const response = await sendMessage();
        if (response?.warning) {
          toast({
            description: response.warning,
            variant: 'warning',
          });
          return;
        }

        const toastMsg = quickQuoteConfig?.isSubmitOnPortalEnabled
          ? 'Bid successfully submitted'
          : 'Filled out quote form. Please review and submit';
        toast({
          description: toastMsg,
          variant: 'success',
        });
      } catch (error: unknown) {
        captureException(error, { functionName: 'handleSubmitToPortal' });

        const portalError =
          error && typeof error === 'object'
            ? (error as { partialSuccess?: boolean; warning?: string })
            : undefined;

        if (portalError?.partialSuccess) {
          toast({
            description:
              'Rate entered. The portal requires you to click Submit.',
            variant: 'info',
          });
          return;
        }

        if (portalError?.warning) {
          toast({
            description: portalError.warning,
            variant: 'warning',
          });
          return;
        }

        toast({
          description: 'Failed to submit quote to portal.',
          variant: 'destructive',
        });
      } finally {
        setIsSubmittingToPortal(false);
      }
    },
    [
      portal,
      toast,
      tabId,
      setIsSubmittingToPortal,
      handleTerminatingAction,
      maxDistance,
      quickQuoteConfig,
      getValues,
      clickedSuggestion,
      email,
      isBiddingPortalSubmitBidEnabled,
      quoteMode,
      selectedLTLQuotes,
      threadReferences,
      inReplyTo,
      waitForSubmitPortalBidResult,
      submitBidLoadIdDirtyRef,
      parentQuoteRequestId,
      quoteResponse?.quoteRequestId,
    ]
  );

  return {
    handleTerminatingAction,
    handleSubmitQuoteToTMS,
    handleDraftResponse,
    handleSendResponse,
    handleSubmitToPortal,
  };
};
